import { useState, useEffect, useRef } from 'react';
import { AppShell } from '../../components/layout/AppShell';
import { KpiCard } from '../../components/kpi/KpiCard';
import { Card } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { EmptyState } from '../../components/ui/EmptyState';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Combobox } from '../../components/ui/Combobox';
import { Toast } from '../../components/ui/Toast';
import { MultiLineChart } from '../../components/charts/MultiLineChart';
import { DriverBars } from '../../components/charts/DriverBars';
import { ProductPicker } from '../../components/charts/ProductPicker';
import { FilterBar } from '../../components/filters/FilterBar';
import { PeriodToggle } from '../../components/filters/PeriodToggle';
import { RangeFilters } from '../../components/filters/RangeFilters';
import { useApiData } from '../../hooks/useApiData';
import { useOptions } from '../../hooks/useOptions';
import { useDataRefresh } from '../../context/DataRefreshContext';
import { formatNumber, formatPercent } from '../../utils/formatters';
import { csvCell, downloadText } from '../../utils/exportCsv';
import { colorForProduct } from '../../config/productColors';
import { dayTickLabel, periodDisplay } from '../../utils/periods';
import { api } from '../../lib/api';

const EMPTY_PRODUCT_FORM = { name: '', family: '' };

// "Add Product" — registers a new row in BigQuery's dim_products so it shows up
// in the options list, dashboards and System Status checks from the next data
// run onward. Usage rows for it still need a separate AppScript writing into
// usage_daily — this only creates the product definition, not its usage history.
function AddProductModal({ open, onClose, families, onCreated }) {
  const [form, setForm] = useState(EMPTY_PRODUCT_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (open) { setForm(EMPTY_PRODUCT_FORM); setError(''); } }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/api/catalog/products', { name: form.name, family: form.family });
      onCreated(form.name);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add product">
      <form onSubmit={submit} className="space-y-4">
        <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
          Adds this product. Its AppScript still needs to be set up separately to deliver usage data.
        </p>
        <Input label="Product name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus />
        <Combobox
          label="Product family"
          value={form.family}
          onChange={(v) => setForm({ ...form, family: v })}
          options={families}
          placeholder="Pick an existing family or type a new one"
          searchPlaceholder="Search or type a new family…"
          creatable
        />
        {error && <Alert>{error}</Alert>}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving || !form.name.trim() || !form.family.trim()}>
            {saving ? 'Adding…' : 'Add product'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

const PERIOD_LABEL = { day: 'Daily', month: 'Monthly', year: 'Yearly' };

// Compact panel shown when a Products row is tapped: which resellers drive this product,
// ranked by their avg/day for it (paginated), from the already-loaded reseller × product matrix.
function ProductMiniDetail({ name, productReseller, color }) {
  const items = (productReseller?.rows || []).map((r) => ({ name: r.reseller, value: r.cells?.[name] || 0 }));
  return <DriverBars items={items} countLabel="Resellers using" topLabel="Top reseller" colorOf={() => color} />;
}

export default function Products() {
  const { prefetch, refresh } = useDataRefresh();
  const { productFamilies: families, dateRange } = useOptions();
  const [productFamily, setProductFamily] = useState('');
  const [granularity, setGranularity] = useState('day');
  const [range, setRange] = useState({ from: '', to: '', preset: '' });
  const [addOpen, setAddOpen] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!msg) return undefined;
    const t = setTimeout(() => setMsg(null), 4500);
    return () => clearTimeout(t);
  }, [msg]);

  const onProductCreated = (name) => {
    setAddOpen(false);
    refresh();
    setMsg({ type: 'success', title: 'Product added', text: `"${name}" has been added — remember to set up its AppScript to start delivering usage data.` });
  };
  const { from, to } = range;

  // On first load, default to the LAST 7 DAYS (From = latest − 6 days, To = latest)
  // with Daily grouping, so the per-product line chart opens on a week of trend.
  // One-time only, so the user can then pick any range/preset without it snapping back.
  const initRange = useRef(false);
  useEffect(() => {
    if (!initRange.current && dateRange?.max) {
      const [y, mo, d] = dateRange.max.split('-').map(Number);
      const start = new Date(y, mo - 1, d - 6);
      const pad = (n) => String(n).padStart(2, '0');
      const fromStr = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
      setRange({ from: fromStr, to: dateRange.max, preset: '' });
      initRange.current = true;
    }
  }, [dateRange]);

  // Detailed CSV: filters, per-period detail, summary, and product breakdown.
  const doExport = (data) => {
    if (!data) return;
    const lines = [];
    const row = (a) => lines.push(a.map(csvCell).join(','));
    row(['CysOps Product Analysis']);
    row(['Product family', productFamily || 'All products']);
    row(['Grouping', granularity]);
    row(['Range', from || to ? `${from || 'start'} to ${to || 'end'}` : 'All time']);
    row(['Generated', new Date().toLocaleString()]);
    lines.push('');

    // 1) Detailed period × product FIRST — one row per product per period (grouped
    //    by product, dates ascending), following the grouping (daily/monthly/yearly).
    const detail = data.detail || [];
    if (detail.length) {
      row([`${PERIOD_LABEL[granularity] || 'Periodic'} Usage - Product x Period (detailed)`]);
      row(['Product', 'Period', 'Usage']);
      detail.forEach((d) => row([d.product, d.period, d.total]));
      lines.push('');
    }

    // 2) Summary
    row(['Summary']);
    row(['Total Usage', data.kpis.totalUsage]);
    row(['Active Resellers', data.kpis.activeResellers]);
    row(['Top Product', data.kpis.topProduct]);
    lines.push('');

    // 3) Product usage — total over the range AND the average per active day
    row([productFamily ? `Products in ${productFamily}` : 'Product Usage']);
    row(['Product', 'Total usage', 'Usage (avg/day)', 'Share %']);
    (data.byProduct || []).forEach((p) => row([p.name, p.sum, p.total, p.share]));
    lines.push('');

    const fam = productFamily ? productFamily.replace(/[^\w-]+/g, '-') : 'all-products';
    const rng = from || to ? `${from || 'start'}_${to || 'end'}` : 'all-time';
    downloadText(`cysops-product-analysis_${fam}_${rng}.csv`, lines.join('\n'), 'text/csv');
  };

  const { data, loading, error } = useApiData('/api/data/products', { productFamily, granularity, from, to });

  // Once the current view has loaded, predictively warm each product family
  // (current grouping + period) in the background so the next click is instant.
  // Gated on !loading so it never competes with the data you're actually viewing.
  useEffect(() => {
    if (loading) return;
    families.forEach((fam) => prefetch('/api/data/products', { productFamily: fam, granularity, from, to }));
  }, [families, granularity, from, to, loading, prefetch]);

  // Pivot the period × product detail into a per-period table: one row per period
  // (day / month / year, per the grouping), a column per product, plus a Total.
  // A single family → that family's products; "All products" → every product.
  const detail = data?.detail || [];
  const ppTotals = {};
  detail.forEach((d) => { ppTotals[d.product] = (ppTotals[d.product] || 0) + d.total; });
  const ppProducts = Object.keys(ppTotals).sort((a, b) => ppTotals[b] - ppTotals[a]);
  const ppRowsMap = new Map();
  detail.forEach((d) => {
    if (!ppRowsMap.has(d.period)) ppRowsMap.set(d.period, { period: d.period, total: 0, cells: {} });
    const r = ppRowsMap.get(d.period);
    r.cells[d.product] = d.total;
    r.total += d.total;
  });
  // 'YYYY-MM-DD' / 'YYYY-MM' / 'YYYY' are zero-padded → lexical sort is chronological.
  const ppRows = [...ppRowsMap.values()].sort((a, b) => (a.period < b.period ? -1 : 1));
  const periodLabel = PERIOD_LABEL[granularity] || 'Per-Period';

  // Product Usage as one colour-coded line per product per period, with fixed colours.
  const colorFor = {};
  ppProducts.forEach((n) => { colorFor[n] = colorForProduct(n); });
  const chartData = ppRows.map((r) => ({ period: r.period, ...r.cells }));
  // Which products to draw. `null` = use the DEFAULT (all of a filtered family's products,
  // or the top 7 for "All products"); an array = the user's explicit picks. Reset to the
  // default whenever the family filter changes. The shown selection is DERIVED from the
  // current products, so it never shows a stale set while the new family's data loads.
  const [selProducts, setSelProducts] = useState(null);
  const [activeProduct, setActiveProduct] = useState(null);
  useEffect(() => { setSelProducts(null); }, [productFamily]);
  const defaultSel = productFamily ? ppProducts : ppProducts.slice(0, 7);
  const effectiveSel = selProducts ?? defaultSel;
  const seriesToDraw = ppProducts.filter((n) => effectiveSel.includes(n));
  const toggleProduct = (n) => setSelProducts((c) => {
    const base = c ?? defaultSel;
    return base.includes(n) ? base.filter((x) => x !== n) : [...base, n];
  });

  return (
    <AppShell title="Product Analysis">
      <FilterBar>
        <div className="w-full sm:w-56">
          <Select
            label="Product family"
            placeholder="All products"
            value={productFamily}
            onChange={(e) => setProductFamily(e.target.value)}
            options={families}
          />
        </div>
        <div>
          <span className="block mb-1 text-sm font-medium text-slate-600 dark:text-slate-300">Grouping</span>
          <PeriodToggle value={granularity} onChange={setGranularity} />
        </div>
        <RangeFilters from={from} to={to} preset={range.preset} dateRange={dateRange} onChange={setRange} />
        <div className="ml-auto flex gap-2">
          <Button onClick={() => setAddOpen(true)} className="gap-1.5">
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M10 4v12M4 10h12" strokeLinecap="round" /></svg>
            Add product
          </Button>
          <Button variant="secondary" disabled={!data} onClick={() => doExport(data)}>
            Export CSV
          </Button>
        </div>
      </FilterBar>

      {loading && <Spinner />}
      {error && <Alert>{error}</Alert>}
      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <KpiCard label="Total Usage" value={formatNumber(data.kpis.totalUsage)} sub="latest day" />
            <KpiCard label="Active Resellers" value={formatNumber(data.kpis.activeResellers)} />
            <KpiCard label="Top Product" value={data.kpis.topProduct} />
          </div>

          <Card
            title={productFamily ? `Products in ${productFamily}` : 'Product Usage'}
            actions={ppProducts.length ? (
              <ProductPicker
                products={ppProducts}
                selected={effectiveSel}
                colorFor={colorFor}
                onToggle={toggleProduct}
                onSelectAll={() => setSelProducts(ppProducts)}
                onClear={() => setSelProducts([])}
                onHover={setActiveProduct}
              />
            ) : null}
          >
            {data.byProduct && data.byProduct.length ? (
              <>
                {chartData.length && seriesToDraw.length ? (
                  <MultiLineChart
                    data={chartData}
                    series={seriesToDraw}
                    colorFor={colorFor}
                    activeSeries={activeProduct}
                    height={430}
                    xTickFormatter={granularity === 'day' ? dayTickLabel : undefined}
                    note={granularity === 'day' ? undefined : 'average usage per active day'}
                  />
                ) : <EmptyState />}
                <div className="mt-6">
                  <h3 className="font-semibold tracking-tight text-navy-800 dark:text-slate-100">Products</h3>
                  <p className="mb-3 mt-0.5 text-xs text-slate-400 dark:text-slate-500">
                    Average per active day over the whole selected range.
                  </p>
                  <Table
                    columns={[
                      { key: 'name', label: 'Product' },
                      { key: 'total', label: 'Usage (avg/day)', align: 'right', render: (v) => formatNumber(v) },
                      { key: 'share', label: 'Share', align: 'right', render: (v) => formatPercent(v) },
                    ]}
                    rows={data.byProduct}
                    pageSize={10}
                    rowKey={(r) => r.name}
                    expandedContent={(r) => (
                      <ProductMiniDetail
                        name={r.name}
                        productReseller={data.productReseller}
                        color={colorFor[r.name] || colorForProduct(r.name)}
                      />
                    )}
                  />
                </div>
              </>
            ) : <EmptyState />}
          </Card>

          <Card title={`${periodLabel} Breakdown`}>
            {ppRows.length ? (
              <Table
                columns={[
                  { key: 'period', label: 'Period', render: (v) => periodDisplay(v, granularity) },
                  ...ppProducts.map((pn) => ({
                    key: pn, label: pn, align: 'right', render: (v) => formatNumber(v || 0),
                  })),
                  { key: 'total', label: 'Total', align: 'right', render: (v) => formatNumber(v) },
                ]}
                rows={ppRows.map((r) => ({ period: r.period, total: r.total, ...r.cells }))}
                pageSize={12}
                searchable
                searchPlaceholder="Search period…"
              />
            ) : <EmptyState />}
          </Card>

          {productFamily && data.productReseller && data.productReseller.rows.length > 0 && (
            <Card title={`Reseller × Product — ${productFamily}`} subtitle="Each cell is the average per active day over the selected range.">

              <Table
                columns={[
                  { key: 'reseller', label: 'Reseller' },
                  ...data.productReseller.products.map((pn) => ({
                    key: pn, label: pn, align: 'right', render: (v) => formatNumber(v || 0),
                  })),
                  { key: 'total', label: 'Total', align: 'right', render: (v) => formatNumber(v) },
                ]}
                rows={data.productReseller.rows.map((r) => ({ reseller: r.reseller, total: r.total, ...r.cells }))}
                pageSize={10}
                searchable
                searchPlaceholder="Search reseller…"
              />
            </Card>
          )}
        </>
      )}

      <AddProductModal open={addOpen} onClose={() => setAddOpen(false)} families={families} onCreated={onProductCreated} />
      <Toast msg={msg} onDismiss={() => setMsg(null)} />
    </AppShell>
  );
}
